import React, { useState } from 'react';
import {
  Database,
  ShieldCheck,
  Activity,
  Layers,
  Globe2,
  BookOpen,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Clock,
  FileCode2,
  ArrowRight,
  Lock,
  Server,
  Search,
  GitBranch,
  TableProperties,
  Award,
  Sparkles,
  FileCheck2,
  TrendingUp,
  Cpu,
  UserCheck,
  Eye,
  Building2,
  Scale,
  DollarSign,
  PieChart,
  RefreshCw,
  Sliders,
  Terminal,
  ChevronRight,
  Plus
} from 'lucide-react';
import { AppView } from '../../types';
import {
  dataGovernanceStore,
  DataDomain,
  DataAsset,
  BusinessTerm,
  GovernedMetricDefinition,
  DataContract,
  DataProduct,
  AIDataEligibilityPolicy,
  FeatureDefinition
} from '../../services/data/dataGovernanceStore';

interface ChiefDataOfficerCommandCenterProps {
  onNavigateToView: (view: AppView) => void;
}

export const ChiefDataOfficerCommandCenter: React.FC<ChiefDataOfficerCommandCenterProps> = ({
  onNavigateToView
}) => {
  const [activeTab, setActiveTab] = useState<
    | 'overview'
    | 'domains'
    | 'catalog'
    | 'master-data'
    | 'glossary'
    | 'contracts'
    | 'products'
    | 'metrics'
    | 'privacy-access'
    | 'ai-readiness'
    | 'features'
  >('overview');

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDomainFilter, setSelectedDomainFilter] = useState<string>('');
  const [selectedAsset, setSelectedAsset] = useState<DataAsset | null>(null);

  const domains = dataGovernanceStore.getDomains();
  const assets = dataGovernanceStore.searchCatalog(searchQuery, selectedDomainFilter as any);
  const terms = dataGovernanceStore.getBusinessTerms();
  const metrics = dataGovernanceStore.getMetrics();
  const contracts = dataGovernanceStore.getContracts();
  const products = dataGovernanceStore.getProducts();
  const aiPolicies = dataGovernanceStore.getAIEligibility();
  const features = dataGovernanceStore.getFeatures();

  // Metrics Calculations
  const totalAssets = assets.length;
  const certifiedAssets = assets.filter((a) => a.certificationStatus === 'CERTIFIED').length;
  const certifiedProducts = products.filter((p) => p.certificationStatus === 'CERTIFIED').length;
  const activeContracts = contracts.filter((c) => c.status === 'ACTIVE').length;
  const certifiedMetricsCount = metrics.filter((m) => m.status === 'CERTIFIED').length;
  const aiProhibitedCount = aiPolicies.filter((p) => p.overallState === 'PROHIBITED').length;
  const ragEligibleCount = aiPolicies.filter((p) => p.ragAllowed).length;

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#1E293B] font-sans pb-16">
      {/* Top Banner & Header */}
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-20 shadow-2xs">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-indigo-600 text-white flex items-center justify-center shadow-sm">
              <Database className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold tracking-wider text-indigo-600 uppercase bg-indigo-50 px-2.5 py-0.5 rounded-full border border-indigo-100">
                  Data OS 2.0 & Governance
                </span>
                <span className="text-xs text-[#64748B] font-mono">P27 Operating System</span>
              </div>
              <h1 className="text-xl font-black text-[#0F172A] tracking-tight">
                Chief Data Officer (CDO) Command Center
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => onNavigateToView('enterprise-data-catalog')}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-bold flex items-center gap-2 transition-colors cursor-pointer"
            >
              <Search className="w-4 h-4" />
              Ouvrir le Data Catalog
            </button>
            <button
              onClick={() => onNavigateToView('cto-command-center')}
              className="px-4 py-2 bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 rounded-lg text-sm font-semibold flex items-center gap-2 transition-colors cursor-pointer"
            >
              <Server className="w-4 h-4" />
              CTO Architecture OS
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="max-w-7xl mx-auto px-6 overflow-x-auto flex gap-1 border-t border-[#F1F5F9]">
          {[
            { id: 'overview', label: 'Vue Exécutive', icon: PieChart },
            { id: 'domains', label: 'Data Domains', icon: Layers },
            { id: 'catalog', label: 'Data Catalog & Lineage', icon: TableProperties },
            { id: 'master-data', label: 'Master Data & SoR', icon: Building2 },
            { id: 'glossary', label: 'Business Glossary', icon: BookOpen },
            { id: 'contracts', label: 'Data Contracts', icon: FileCheck2 },
            { id: 'products', label: 'Data Products', icon: Award },
            { id: 'metrics', label: 'Gouvernance Métriques', icon: TrendingUp },
            { id: 'privacy-access', label: 'Confidentialité & Accès', icon: Lock },
            { id: 'ai-readiness', label: 'AI Data Readiness & RAG', icon: Sparkles },
            { id: 'features', label: 'Feature Registry ML', icon: Cpu }
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`px-4 py-3 text-sm font-bold flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                  isActive
                    ? 'border-indigo-600 text-indigo-600 bg-indigo-50/50'
                    : 'border-transparent text-[#64748B] hover:text-[#0F172A] hover:bg-slate-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </header>

      {/* Main Container */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* EXECUTIVE OVERVIEW */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              <div className="bg-white p-5 rounded-xl border border-[#E2E8F0] shadow-2xs">
                <div className="text-xs font-semibold text-[#64748B] uppercase">Data Assets</div>
                <div className="text-2xl font-black text-[#0F172A] mt-1">{totalAssets}</div>
                <div className="text-xs text-indigo-600 font-semibold mt-1">{certifiedAssets} certifiés</div>
              </div>

              <div className="bg-white p-5 rounded-xl border border-[#E2E8F0] shadow-2xs">
                <div className="text-xs font-semibold text-[#64748B] uppercase">Data Products</div>
                <div className="text-2xl font-black text-emerald-600 mt-1">{products.length}</div>
                <div className="text-xs text-emerald-700 font-semibold mt-1">{certifiedProducts} 100% qualifiés</div>
              </div>

              <div className="bg-white p-5 rounded-xl border border-[#E2E8F0] shadow-2xs">
                <div className="text-xs font-semibold text-[#64748B] uppercase">Data Contracts</div>
                <div className="text-2xl font-black text-blue-600 mt-1">{contracts.length}</div>
                <div className="text-xs text-blue-700 font-semibold mt-1">{activeContracts} actifs</div>
              </div>

              <div className="bg-white p-5 rounded-xl border border-[#E2E8F0] shadow-2xs">
                <div className="text-xs font-semibold text-[#64748B] uppercase">Métriques Certifiées</div>
                <div className="text-2xl font-black text-purple-600 mt-1">{certifiedMetricsCount}</div>
                <div className="text-xs text-purple-700 font-semibold mt-1">SLA unique révisé</div>
              </div>

              <div className="bg-white p-5 rounded-xl border border-[#E2E8F0] shadow-2xs">
                <div className="text-xs font-semibold text-[#64748B] uppercase">Datasets RAG-Ready</div>
                <div className="text-2xl font-black text-amber-600 mt-1">{ragEligibleCount}</div>
                <div className="text-xs text-amber-700 font-semibold mt-1">Contrats RAG actifs</div>
              </div>

              <div className="bg-white p-5 rounded-xl border border-[#E2E8F0] shadow-2xs">
                <div className="text-xs font-semibold text-[#64748B] uppercase">AI Prohibited PII</div>
                <div className="text-2xl font-black text-rose-600 mt-1">{aiProhibitedCount}</div>
                <div className="text-xs text-rose-700 font-semibold mt-1">Bloqués à 100%</div>
              </div>
            </div>

            {/* Strategic Pillars Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-[#0F172A] flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-indigo-600" />
                    Principes Fondateurs Data OS YamooPay
                  </h2>
                  <span className="px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-bold rounded-full">
                    Gouvernance Active
                  </span>
                </div>

                <div className="space-y-3 text-sm text-[#475569]">
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <strong className="text-slate-900 block font-bold">1. DATA ≠ INFORMATION ≠ TRUSTED DATA</strong>
                    Une table brute sans Data Steward ni contrat validé ne peut pas accéder au statut Certified.
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <strong className="text-slate-900 block font-bold">2. METRIC FORMULA ≠ DASHBOARD QUERY</strong>
                    Les métriques clés (MRR, MAU, Tax Rate) sont définies indépendamment des tableaux de bord.
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <strong className="text-slate-900 block font-bold">3. DATA AUTHORIZED ≠ DATA AUTHORIZED FOR AI</strong>
                    Une autorisation d&apos;analyse BI n&apos;octroie aucun droit automatique d&apos;entraînement d&apos;IA ou RAG.
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-[#0F172A] flex items-center gap-2">
                    <Activity className="w-5 h-5 text-emerald-600" />
                    Santé Globale des Data Domains
                  </h2>
                </div>

                <div className="space-y-4">
                  {domains.slice(0, 4).map((domain) => (
                    <div key={domain.id} className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-200">
                      <div>
                        <div className="font-bold text-[#0F172A] text-sm">{domain.name}</div>
                        <div className="text-xs text-[#64748B]">
                          Steward: {domain.dataSteward} • {domain.assetCount} assets
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-black text-emerald-600">{domain.qualityScorePct}% Qualité</div>
                        <div className="text-xs text-[#64748B]">{domain.certifiedProductsCount} produits certifiés</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* DATA DOMAINS */}
        {activeTab === 'domains' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-[#0F172A]">Registre des Data Domains</h2>
                <p className="text-sm text-[#64748B]">
                  Chaque Data Domain possède un Business Owner, un Technical Owner et un Data Steward désignés.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {domains.map((dom) => (
                <div key={dom.id} className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs hover:border-indigo-300 transition-colors">
                  <div className="flex items-center justify-between mb-3">
                    <span className="px-2.5 py-1 bg-indigo-50 text-indigo-700 text-xs font-bold rounded-full border border-indigo-100 uppercase">
                      {dom.id}
                    </span>
                    <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-100">
                      {dom.qualityScorePct}% Qualité
                    </span>
                  </div>

                  <h3 className="text-lg font-bold text-[#0F172A] mb-2">{dom.name}</h3>
                  <p className="text-xs text-[#475569] mb-4 min-h-[36px]">{dom.description}</p>

                  <div className="border-t border-[#F1F5F9] pt-3 space-y-1.5 text-xs text-[#64748B]">
                    <div><strong className="text-[#0F172A]">Business Owner:</strong> {dom.businessOwner}</div>
                    <div><strong className="text-[#0F172A]">Technical Owner:</strong> {dom.technicalOwner}</div>
                    <div><strong className="text-[#0F172A]">Data Steward:</strong> {dom.dataSteward}</div>
                  </div>

                  <div className="mt-4 pt-3 border-t border-[#F1F5F9] flex items-center justify-between text-xs font-bold text-slate-700">
                    <span>{dom.assetCount} Data Assets</span>
                    <span>{dom.activeContractsCount} Contrats</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ENTERPRISE DATA CATALOG */}
        {activeTab === 'catalog' && (
          <div className="space-y-6">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-white p-4 rounded-xl border border-[#E2E8F0]">
              <div className="relative flex-1 w-full">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                <input
                  type="text"
                  placeholder="Rechercher par nom d'asset, description, propriétaire ou SQL..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-indigo-500"
                />
              </div>

              <select
                value={selectedDomainFilter}
                onChange={(e) => setSelectedDomainFilter(e.target.value)}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none"
              >
                <option value="">Tous les Data Domains</option>
                {domains.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>

            <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-2xs overflow-hidden">
              <div className="p-4 border-b border-[#E2E8F0] font-bold text-slate-800 text-sm flex justify-between items-center">
                <span>Data Assets ({assets.length})</span>
                <span className="text-xs text-slate-500">Metadata strictly governed via PostgreSQL</span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-xs uppercase border-b border-slate-200">
                    <tr>
                      <th className="p-4">Asset Name & Type</th>
                      <th className="p-4">Domain</th>
                      <th className="p-4">Owner & Steward</th>
                      <th className="p-4">Quality Tier</th>
                      <th className="p-4">Classification</th>
                      <th className="p-4">Status</th>
                      <th className="p-4 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-700">
                    {assets.map((asset) => (
                      <tr key={asset.id} className="hover:bg-slate-50/80 transition-colors">
                        <td className="p-4 font-mono font-bold text-indigo-700">
                          {asset.name}
                          <div className="text-xs font-sans text-slate-500 font-normal mt-0.5">{asset.description}</div>
                        </td>
                        <td className="p-4">
                          <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-xs font-semibold">
                            {asset.domain}
                          </span>
                        </td>
                        <td className="p-4 text-xs">
                          <div className="font-semibold text-slate-900">{asset.owner}</div>
                          <div className="text-slate-500">{asset.steward}</div>
                        </td>
                        <td className="p-4">
                          <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200">
                            {asset.qualityTier}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                            asset.classification === 'RESTRICTED' ? 'bg-rose-50 text-rose-700 border border-rose-200' : 'bg-slate-100 text-slate-700'
                          }`}>
                            {asset.classification}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                            <CheckCircle2 className="w-3 h-3" />
                            {asset.certificationStatus}
                          </span>
                        </td>
                        <td className="p-4 text-right">
                          <button
                            onClick={() => setSelectedAsset(asset)}
                            className="px-3 py-1 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold rounded-lg transition-colors cursor-pointer"
                          >
                            Détails & Champs
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Selected Asset Modal / Detail Drawer */}
            {selectedAsset && (
              <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-6 shadow-xl border border-slate-200">
                  <div className="flex justify-between items-start border-b border-slate-200 pb-4">
                    <div>
                      <span className="px-2.5 py-0.5 bg-indigo-50 text-indigo-700 text-xs font-bold rounded-full border border-indigo-200 uppercase">
                        {selectedAsset.type}
                      </span>
                      <h3 className="text-xl font-bold font-mono text-slate-900 mt-1">{selectedAsset.name}</h3>
                      <p className="text-sm text-slate-600 mt-0.5">{selectedAsset.businessDefinition}</p>
                    </div>
                    <button
                      onClick={() => setSelectedAsset(null)}
                      className="text-slate-400 hover:text-slate-600 text-lg font-bold cursor-pointer"
                    >
                      ✕
                    </button>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-bold text-slate-900 text-sm">Dictionnaire des Champs ({selectedAsset.fields.length})</h4>
                    <div className="border border-slate-200 rounded-xl overflow-hidden">
                      <table className="w-full text-left text-xs">
                        <thead className="bg-slate-50 font-bold text-slate-700 border-b border-slate-200">
                          <tr>
                            <th className="p-3">Field Name</th>
                            <th className="p-3">Physical Type</th>
                            <th className="p-3">Semantic Type</th>
                            <th className="p-3">Sensitivity</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {selectedAsset.fields.map((f, i) => (
                            <tr key={i}>
                              <td className="p-3 font-mono font-bold text-slate-900">{f.name}</td>
                              <td className="p-3 font-mono text-slate-600">{f.physicalType}</td>
                              <td className="p-3 font-semibold text-indigo-600">{f.semanticType}</td>
                              <td className="p-3">
                                <span className={`px-2 py-0.5 rounded font-bold ${f.sensitivity === 'HIGH' ? 'bg-rose-50 text-rose-700' : 'bg-slate-100 text-slate-700'}`}>
                                  {f.sensitivity}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="flex justify-end pt-4 border-t border-slate-200">
                    <button
                      onClick={() => setSelectedAsset(null)}
                      className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-bold cursor-pointer"
                    >
                      Fermer
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* MASTER DATA MANAGEMENT */}
        {activeTab === 'master-data' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
              <h2 className="text-lg font-bold text-slate-900 mb-2">System of Record & Master Data Registry</h2>
              <p className="text-sm text-slate-600 mb-6">
                Chaque entité maître (Devises, Pays, Marchés, Tax Rulesets) dispose d&apos;un System of Record officiel et de règles de résolution de conflit explicites.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-4 border border-slate-200 rounded-xl bg-slate-50/50">
                  <div className="font-bold text-slate-900 text-sm mb-1">Currency Master Data</div>
                  <div className="text-xs text-slate-500 mb-3">System of Record: Prompt 04 & Central Banks Ingestion</div>
                  <div className="text-xs space-y-1 text-slate-700">
                    <div>• <strong>Primary Key:</strong> ISO 4217 Currency Code (ex: EUR, USD, XOF)</div>
                    <div>• <strong>Survivorship:</strong> Authority feed (ECB/BCEAO) overrides internal defaults</div>
                    <div>• <strong>Conflict Resolution:</strong> Automated quarantine on &gt;3% intraday rate drift</div>
                  </div>
                </div>

                <div className="p-4 border border-slate-200 rounded-xl bg-slate-50/50">
                  <div className="font-bold text-slate-900 text-sm mb-1">Country & Jurisdiction Master</div>
                  <div className="text-xs text-slate-500 mb-3">System of Record: Prompt 03 & 22 Governance Tables</div>
                  <div className="text-xs space-y-1 text-slate-700">
                    <div>• <strong>Primary Key:</strong> ISO 3166-1 alpha-2 (ex: FR, US, SN)</div>
                    <div>• <strong>Golden Record:</strong> Strict single definition for tax, compliance & SEO</div>
                    <div>• <strong>Conflict Resolution:</strong> Manual Legal Steward review for sanctions changes</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* BUSINESS GLOSSARY */}
        {activeTab === 'glossary' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Enterprise Business Glossary</h2>
                <p className="text-sm text-slate-600">Définitions d&apos;affaires canoniques validées par le Data Governance Board.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {terms.map((term) => (
                <div key={term.id} className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
                  <div className="flex items-center justify-between mb-3">
                    <span className="px-2.5 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-bold rounded-full border border-emerald-200">
                      {term.status}
                    </span>
                    <span className="text-xs text-slate-400 font-mono">Maj: {term.lastUpdated}</span>
                  </div>

                  <h3 className="text-lg font-bold text-slate-900 mb-2">{term.term}</h3>
                  <p className="text-sm text-slate-600 mb-4">{term.definition}</p>

                  <div className="border-t border-slate-100 pt-3 text-xs text-slate-500 space-y-1">
                    <div><strong className="text-slate-800">Owner:</strong> {term.owner}</div>
                    <div><strong className="text-slate-800">Approved by:</strong> {term.approvedBy}</div>
                    <div><strong className="text-slate-800">Synonyms:</strong> {term.synonyms.join(', ')}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* DATA CONTRACTS */}
        {activeTab === 'contracts' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
              <h2 className="text-lg font-bold text-slate-900 mb-1">Contrats de Données Actifs (Data Contracts)</h2>
              <p className="text-sm text-slate-600 mb-6">
                Accords formels entre producteurs et consommateurs garantissant le schéma, la fraîcheur et la qualité.
              </p>

              <div className="space-y-4">
                {contracts.map((c) => (
                  <div key={c.id} className="p-4 rounded-xl border border-slate-200 bg-slate-50/50 flex flex-col md:flex-row justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="px-2.5 py-0.5 bg-blue-50 text-blue-700 font-bold text-xs rounded-full border border-blue-200">
                          {c.status}
                        </span>
                        <span className="font-mono text-xs text-slate-500">v{c.schemaVersion}</span>
                      </div>
                      <h3 className="text-base font-bold text-slate-900 mt-1">{c.title}</h3>
                      <p className="text-xs text-slate-600 mt-0.5">{c.semanticDefinition}</p>
                      <div className="text-xs text-slate-500 mt-2">
                        Producteur: <strong className="text-slate-800">{c.producer}</strong> • Consommateurs: <strong className="text-slate-800">{c.consumers.join(', ')}</strong>
                      </div>
                    </div>

                    <div className="text-right flex flex-col justify-between">
                      <div className="text-xs font-bold text-emerald-600 bg-emerald-50 px-3 py-1 rounded-lg border border-emerald-200 inline-block">
                        Seuil Qualité: {c.qualityThresholdPct}%
                      </div>
                      <div className="text-xs text-slate-500">Fraîcheur attendue: &lt; {c.expectedFreshnessMinutes} min</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* DATA PRODUCTS */}
        {activeTab === 'products' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Plateforme de Data Products Certifiés</h2>
                <p className="text-sm text-slate-600">Composants de données réutilisables à valeur ajoutée métier.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {products.map((p) => (
                <div key={p.id} className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
                  <div className="flex justify-between items-center mb-3">
                    <span className="px-3 py-1 bg-emerald-50 text-emerald-700 text-xs font-bold rounded-full border border-emerald-200 flex items-center gap-1">
                      <Award className="w-3.5 h-3.5" />
                      {p.certificationStatus}
                    </span>
                    <span className="text-xs font-mono text-slate-500">${p.estimatedMonthlyCostUSD}/mois</span>
                  </div>

                  <h3 className="text-lg font-bold text-slate-900 mb-1">{p.name}</h3>
                  <p className="text-xs text-slate-600 mb-4">{p.purpose}</p>

                  <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-1 text-xs text-slate-700 mb-4">
                    <div><strong>SLO:</strong> {p.slo}</div>
                    <div><strong>Accès:</strong> {p.accessPolicy}</div>
                    <div><strong>Assets Inclus:</strong> {p.containedAssets.join(', ')}</div>
                  </div>

                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>Owner: <strong>{p.owner}</strong></span>
                    <span className="text-indigo-600 font-bold cursor-pointer hover:underline">Voir README & Docs</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* GOVERNED METRICS */}
        {activeTab === 'metrics' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
              <h2 className="text-lg font-bold text-slate-900 mb-1">Registre des Métriques Certifiées (Semantic Layer)</h2>
              <p className="text-sm text-slate-600 mb-6">
                Chaque KPI stratégique (MRR, MAU, Tax Calculation Rate) possède une formule unique et un propriétaire désigné.
              </p>

              <div className="space-y-4">
                {metrics.map((m) => (
                  <div key={m.id} className="p-4 rounded-xl border border-slate-200 bg-slate-50/50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-bold bg-purple-50 text-purple-700 px-2.5 py-0.5 rounded border border-purple-200">
                          {m.key}
                        </span>
                        <span className="text-xs text-slate-500 font-mono">{m.version}</span>
                      </div>
                      <span className="px-2.5 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-bold rounded-full border border-emerald-200">
                        {m.status}
                      </span>
                    </div>

                    <h3 className="text-base font-bold text-slate-900 mt-2">{m.name}</h3>
                    <p className="text-xs text-slate-600 mt-0.5">{m.description}</p>

                    <div className="mt-3 p-3 bg-slate-900 text-slate-100 rounded-lg font-mono text-xs overflow-x-auto">
                      Formule SQL Certifiée: {m.formula}
                    </div>

                    <div className="mt-3 text-xs text-slate-500 flex flex-wrap gap-4 border-t border-slate-200 pt-2">
                      <span>Grain: <strong className="text-slate-800">{m.grain}</strong></span>
                      <span>Business Owner: <strong className="text-slate-800">{m.businessOwner}</strong></span>
                      <span>Unité: <strong className="text-slate-800">{m.unit}</strong></span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* PRIVACY & ACCESS */}
        {activeTab === 'privacy-access' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
              <h2 className="text-lg font-bold text-slate-900 mb-2">Gouvernance de la Confidentialité & Masquage</h2>
              <p className="text-sm text-slate-600 mb-6">
                Application stricte du principe de moindre privilège, masquage pseudonymisé et lien automatique avec la politique de rétention RGPD.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="p-4 border border-rose-200 bg-rose-50/40 rounded-xl">
                  <div className="font-bold text-rose-900 text-sm mb-1">Protection PII Critique</div>
                  <div className="text-xs text-rose-700">Emails, hashes et identifiants masqués systématiquement pour les analystes BI.</div>
                </div>

                <div className="p-4 border border-indigo-200 bg-indigo-50/40 rounded-xl">
                  <div className="font-bold text-indigo-900 text-sm mb-1">Accès par Finalité</div>
                  <div className="text-xs text-indigo-700">Toute demande d&apos;accès requiert une justification métier avec expiration automatique à 30 jours.</div>
                </div>

                <div className="p-4 border border-emerald-200 bg-emerald-50/40 rounded-xl">
                  <div className="font-bold text-emerald-900 text-sm mb-1">Legal Hold Protection</div>
                  <div className="text-xs text-emerald-700">Suppression automatisée suspendue immédiatement en cas de rétention légale active (Prompt 22).</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* AI DATA READINESS */}
        {activeTab === 'ai-readiness' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
              <h2 className="text-lg font-bold text-slate-900 mb-1">Matrice d&apos;Éligibilité AI Data Readiness</h2>
              <p className="text-sm text-slate-600 mb-6">
                Une donnée accessible ne donne aucun droit d&apos;utilisation IA automatique. Définition explicite des autorisations RAG vs Entraînement.
              </p>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-xs uppercase border-b border-slate-200">
                    <tr>
                      <th className="p-4">Asset Name</th>
                      <th className="p-4">RAG Allowed?</th>
                      <th className="p-4">Embedding Allowed?</th>
                      <th className="p-4">Training Allowed?</th>
                      <th className="p-4">Statut Global</th>
                      <th className="p-4">Justification</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-700">
                    {aiPolicies.map((pol, idx) => (
                      <tr key={idx}>
                        <td className="p-4 font-mono font-bold text-slate-900">{pol.assetName}</td>
                        <td className="p-4">
                          {pol.ragAllowed ? (
                            <span className="text-emerald-600 font-bold text-xs bg-emerald-50 px-2 py-0.5 rounded">OUI</span>
                          ) : (
                            <span className="text-rose-600 font-bold text-xs bg-rose-50 px-2 py-0.5 rounded">NON</span>
                          )}
                        </td>
                        <td className="p-4">
                          {pol.embeddingAllowed ? (
                            <span className="text-emerald-600 font-bold text-xs bg-emerald-50 px-2 py-0.5 rounded">OUI</span>
                          ) : (
                            <span className="text-rose-600 font-bold text-xs bg-rose-50 px-2 py-0.5 rounded">NON</span>
                          )}
                        </td>
                        <td className="p-4">
                          {pol.trainingAllowed ? (
                            <span className="text-emerald-600 font-bold text-xs bg-emerald-50 px-2 py-0.5 rounded">OUI</span>
                          ) : (
                            <span className="text-rose-600 font-bold text-xs bg-rose-50 px-2 py-0.5 rounded">NON</span>
                          )}
                        </td>
                        <td className="p-4">
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${
                            pol.overallState === 'PROHIBITED' ? 'bg-rose-100 text-rose-800' : 'bg-indigo-50 text-indigo-700'
                          }`}>
                            {pol.overallState}
                          </span>
                        </td>
                        <td className="p-4 text-xs text-slate-600 max-w-xs">{pol.justification}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* FEATURE REGISTRY */}
        {activeTab === 'features' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
              <h2 className="text-lg font-bold text-slate-900 mb-1">Registre des Features ML & Data Foundations</h2>
              <p className="text-sm text-slate-600 mb-6">
                Catalogue des caractéristiques dérivées pour l&apos;apprentissage automatique et l&apos;intelligence prédictive.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {features.map((feat) => (
                  <div key={feat.id} className="p-5 border border-slate-200 rounded-xl bg-slate-50/50">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-mono text-xs font-bold text-indigo-700 bg-indigo-50 px-2.5 py-0.5 rounded">
                        {feat.featureName}
                      </span>
                      <span className="text-xs text-slate-500 font-mono">Entité: {feat.entityName}</span>
                    </div>

                    <p className="text-xs text-slate-600 mb-3">{feat.definition}</p>

                    <div className="p-2.5 bg-slate-900 text-slate-100 rounded font-mono text-xs mb-3">
                      {feat.transformation}
                    </div>

                    <div className="text-xs text-slate-500 flex justify-between border-t border-slate-200 pt-2">
                      <span>Owner: <strong>{feat.owner}</strong></span>
                      <span>Online Serving: <strong>{feat.isOnlineAvailable ? 'Disponible' : 'Non'}</strong></span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
